"use client";

import { _Media_liquid } from "/_types";
import { ChevronLeftIcon, ChevronRightIcon, CloseIcon } from "/icons";
import { Image } from "/image";
import { useWindowSize } from "/scrollbar";
import { scrollToX } from "/utils";
import clsx from "clsx";
import { FC, Fragment, useEffect, useRef, useState } from "react";
import { create } from "zustand";
import { immer } from "zustand/middleware/immer";

export type ImagePreviewModalState = {
  open: boolean;
  media: _Media_liquid[];
  index: number;
};

export const useImagePreviewModal = create(
  immer<ImagePreviewModalState>((set, get) => {
    /*Initial State*/

    return {
      open: false,
      media: [],
      index: -1,
    };
  })
);

const renderMedia = (media: _Media_liquid, className?: string) => {
  switch (media.media_type) {
    case "image": {
      return (
        <Image
          src={media?.src}
          loading="eager"
          alt={media?.alt}
          aspectRatio={media?.aspect_ratio}
          className={className}
        />
      );
    }
    case "external_video": {
      return (
        <iframe
          frameBorder="0"
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen={true}
          className={clsx("js-youtube border-0 bg-inherit", className)}
          loading="lazy"
          src={
            media.host === "youtube"
              ? `https://www.youtube.com/embed/${
                  media.external_id
                }?autoplay=0&controls=1&muted=1&enablejsapi=1&loop=${1}&modestbranding=1&playsinline=1&rel=0`
              : `https://player.vimeo.com/video/${media.external_id}`
          }
          title={media.alt}
        ></iframe>
      );
    }
    case "model": {
      return (
        <Image
          src={media?.preview_image?.src}
          loading="eager"
          alt={media?.alt}
          aspectRatio={media?.preview_image?.aspect_ratio}
          className={className}
        />
      );
    }
    case "video": {
      return (
        <video
          playsInline={true}
          controls={true}
          autoPlay={true}
          loop={true}
          preload="none"
          className={className}
          muted={true}
          aria-label={media.alt}
          poster={media.preview_image.src}
        >
          <source
            src={media?.sources?.find((src) => src.format === "mp4" && src.width <= 852)?.url}
            type="video/mp4"
          />

          <Image src={media.preview_image.src} alt={media.alt} screenPercentage={60} />
        </video>
      );
    }
  }
};

export const ImagePreviewModal: FC = () => {
  const { open, media, index } = useImagePreviewModal();
  const [animate, setAnimate] = useState(false);
  const [opened, setOpened] = useState(false);

  const mobileScrollContainerRef = useRef<HTMLDivElement>(null);
  const { width } = useWindowSize();

  const image = media[index];

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        if (width < 768 && mobileScrollContainerRef.current) {
          scrollToX(
            0,
            (mobileScrollContainerRef.current.scrollWidth / media.length) * index,
            mobileScrollContainerRef.current
          );
        }
      }, 75);
    }
  }, [index, media.length, open, width]);

  useEffect(() => {
    if (open && !animate && !opened) {
      setAnimate(true);
      setTimeout(() => {
        setOpened(true);
      }, 1);
      setTimeout(() => {
        setAnimate(false);
      }, 155);
    }
    if (!open && !animate && opened) {
      setAnimate(true);
      setTimeout(() => {
        setOpened(false);
      }, 1);
      setTimeout(() => {
        setAnimate(false);
      }, 155);
    }
  }, [open, animate, opened]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        useImagePreviewModal.setState({ open: false });
      }
    };
    if (open || animate) {
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.addEventListener("keydown", handleKeyDown);
      };
    } else {
      document.removeEventListener("keydown", handleKeyDown);
    }
  }, [animate, open]);

  if (!image) {
    return null;
  }
  if (!open && !animate) {
    return null;
  }
  return (
    <div className={clsx("fixed inset-0 z-[2147483647] flex max-h-screen flex-col")}>
      <section
        className={clsx("transition-all duration-150", opened ? "opacity-100" : "opacity-0")}
      >
        <div className="relative z-[110] bg-black text-white">
          <section>
            <header className="container grid max-w-none grid-cols-[100px_1fr_100px] gap-2 py-6">
              <div>
                <button
                  className="inline-flex items-center gap-2 rounded px-4 py-2 outline-none hf:bg-gray-900/70 hf:outline-none hf:ring-0"
                  type="button"
                  onClick={() => {
                    useImagePreviewModal.setState({ open: false });
                  }}
                >
                  <CloseIcon className="h-4 w-4" />
                  Close
                </button>
              </div>
              <div className="flex items-center justify-center">
                {index + 1} / {media.length}
              </div>
            </header>
            <main className="container grid w-full max-w-none flex-1 shrink-0 gap-2 md:grid-cols-[100px_1fr_100px]">
              <div className="flex h-full items-center justify-center max-md:hidden">
                <button
                  type="button"
                  className={clsx(
                    "rounded-full border border-white p-3 hf:bg-gray-900/70",
                    index <= 0 && "hidden"
                  )}
                  disabled={index <= 0}
                  onClick={() => {
                    useImagePreviewModal.setState({
                      index: index - 1,
                    });
                  }}
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                </button>
              </div>
              <div className="relative flex h-full max-h-[calc(100vh-88px-96px)] w-full items-center justify-center">
                {image
                  ? renderMedia(image, "relative h-full w-full object-contain max-md:hidden")
                  : null}
                <div
                  className="scrollbar-none grid max-h-[calc(100vh-88px-96px)] w-[calc(100vw-32px)] snap-x snap-mandatory gap-4 overflow-x-auto md:pointer-events-none md:absolute md:inset-0 md:opacity-0"
                  style={{ gridTemplateColumns: `repeat(${media.length}, calc(100vw - 32px))` }}
                  ref={mobileScrollContainerRef}
                  onScroll={(e) => {
                    if (width < 768) {
                      const newIndex = Math.floor(
                        e.currentTarget.scrollLeft / (e.currentTarget.scrollWidth / media.length)
                      );
                      useImagePreviewModal.setState({
                        index: newIndex,
                      });
                    }
                  }}
                >
                  {media.map((image, index) => {
                    return (
                      <picture
                        className="relative h-full max-h-[calc(100vh-88px-96px)] w-full snap-center"
                        key={index}
                      >
                        {renderMedia(image, "inset-0 h-full w-full object-contain md:absolute")}
                      </picture>
                    );
                  })}
                </div>
              </div>
              <div className="flex h-full items-center justify-center max-md:hidden">
                <button
                  type="button"
                  className={clsx(
                    "rounded-full border border-white p-3 hf:bg-gray-900/70",
                    index === media.length - 1 && "hidden"
                  )}
                  disabled={index === media.length - 1}
                  onClick={() => {
                    useImagePreviewModal.setState({
                      index: index + 1,
                    });
                  }}
                >
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
              </div>
            </main>
            <footer className="text-balance container flex h-24 max-w-3xl items-center justify-center">
              {image?.alt}
            </footer>
          </section>
        </div>
      </section>
    </div>
  );
};
